import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useCallback, useMemo } from 'react'

import { isLabelGroupId } from './labelGroups'
import {
  buildProcessDataLabelEntry,
  buildProcessDataLabelPatch,
  buildProcessDataLabelsLoadOptions,
  generateProcessDataLabelID,
  parseProcessDataLabels,
  type ProcessDataLabel,
} from './processDataLabelsService'

export interface UseProcessDataLabelsResult {
  labels: ProcessDataLabel[]
  isLoading: boolean
  createLabel: (
    input: Omit<ProcessDataLabel, 'id'>
  ) => Promise<ProcessDataLabel>
  updateLabel: (label: ProcessDataLabel) => Promise<void>
  deleteLabel: (id: string) => Promise<void>
}

/**
 * Read/write access to a site's process-data labels.
 *
 * Every mutation revalidates the site's label query before it resolves, so a
 * caller that awaits `createLabel` can render the fresh list immediately. No
 * optimistic cache write: the repo's other Rolodex CRUD paths (`useRolodexSync`,
 * the manual-alert flow) refetch after the write, and a label's rendered
 * geometry comes straight off the stored range.
 */
export const useProcessDataLabels = (
  siteID: string | undefined
): UseProcessDataLabelsResult => {
  const { rolodexPut, rolodexPatch, rolodexDelete } = useRolodexEdit()

  const loadOptions = useMemo(
    () => buildProcessDataLabelsLoadOptions(siteID),
    [siteID]
  )

  const {
    data: entries,
    isLoading,
    mutate,
  } = useGetRolodexEntriesByLoadOptions<unknown[]>(loadOptions)

  const labels = useMemo(() => parseProcessDataLabels(entries), [entries])

  const createLabel = useCallback(
    async (input: Omit<ProcessDataLabel, 'id'>): Promise<ProcessDataLabel> => {
      if (!siteID) {
        throw new Error('Cannot create a process data label without a site')
      }

      const id = generateProcessDataLabelID()
      const entry = buildProcessDataLabelEntry(id, siteID, input)
      await rolodexPut([entry])
      await mutate()

      return {
        id,
        name: entry.data.name,
        description: entry.data.description,
        startMs: entry.data.start_time,
        endMs: entry.data.end_time,
        sensorIds: entry.data.sensor_ids,
        groupId: isLabelGroupId(entry.data.group_id)
          ? entry.data.group_id
          : null,
      }
    },
    [mutate, rolodexPut, siteID]
  )

  const updateLabel = useCallback(
    async (label: ProcessDataLabel): Promise<void> => {
      await rolodexPatch([buildProcessDataLabelPatch(label)])
      await mutate()
    },
    [mutate, rolodexPatch]
  )

  const deleteLabel = useCallback(
    async (id: string): Promise<void> => {
      await rolodexDelete(id)
      await mutate()
    },
    [mutate, rolodexDelete]
  )

  return useMemo(
    () => ({
      labels,
      isLoading,
      createLabel,
      updateLabel,
      deleteLabel,
    }),
    [createLabel, deleteLabel, isLoading, labels, updateLabel]
  )
}
